/**
 * Configuration for a single API endpoint
 */
export interface ApiConfig {
  baseUrl: string;
  apiPrefix?: string; // e.g., '/api' or '/v1'
}

/**
 * Frontend API configuration — the APIs the spog-client uses.
 * All fields required to enforce correct frontend wiring at compile time.
 */
export interface FrontendApiConfigs {
  backendApi: ApiConfig;
}

/**
 * Base API configuration shared by backends.
 */
export interface BackendOnlyAuditApiConfigs {
  auditLogApi: ApiConfig;
}

export interface BackendJobsApiConfigs extends BackendOnlyAuditApiConfigs {
  jobsApi: ApiConfig;
}

/**
 * Backend API configuration — the APIs used by asset-service-layer.
 * All fields required to enforce correct backend wiring at compile time.
 */
export interface BackendAssetApiConfigs extends BackendJobsApiConfigs {
  deviceApi: ApiConfig;
  groupingApi: ApiConfig;
  userApi: ApiConfig;
}

/**
 * Backend API configuration — the APIs used by spog-backend.
 * All fields required to enforce correct backend wiring at compile time.
 */
export interface BackendSpogApiConfigs extends BackendOnlyAuditApiConfigs {
  accessApi: ApiConfig;
  assetServiceLayerApi: ApiConfig;
  gopherBuddyApi: ApiConfig;
}

/**
 * Cloud Run job steps that only call asset-service-layer (export pipeline).
 * Use when the step only needs {@link ApiUrlBuilder.getCloudRunAudienceFor} for `assetServiceLayerApi`.
 */
export interface BackendExportJobStepApiConfigs {
  assetServiceLayerApi: ApiConfig;
}

/**
 * Cloud Run job steps that only call saas-device-api (e.g. validate-import).
 */
export interface BackendDeviceJobStepApiConfigs {
  deviceApi: ApiConfig;
}

type AnyApiConfigs =
  | FrontendApiConfigs
  | BackendOnlyAuditApiConfigs
  | BackendJobsApiConfigs
  | BackendAssetApiConfigs
  | BackendSpogApiConfigs
  | BackendExportJobStepApiConfigs
  | BackendDeviceJobStepApiConfigs;

/** Injection token for ApiUrlBuilder (works with both NestJS and Angular DI) */
export const API_URL_BUILDER = 'API_URL_BUILDER';

/**
 * Generic URL builder for constructing API URLs from route constants
 *
 * Supports:
 * - Multiple route parameters (e.g., :customerId/:deviceId)
 * - Nested routes with multiple params
 * - Root routes (empty strings)
 * - Automatic cleanup of double slashes and trailing slashes
 *
 * @example
 * ```typescript
 * // Frontend usage (spog-client)
 * const builder = new ApiUrlBuilder({
 *   backendApi: { baseUrl: 'http://localhost:3000' },
 * });
 *
 * // Spog-backend usage
 * const spogBuilder = new ApiUrlBuilder({
 *   accessApi: { baseUrl: 'http://localhost:3100' },
 *   assetServiceLayerApi: { baseUrl: 'http://localhost:3050' },
 *   auditLogApi: { baseUrl: 'http://localhost:3900' },
 *   gopherBuddyApi: { baseUrl: 'http://localhost:4000' },
 * });
 *
 * // Asset backend usage (asset-service-layer, jobs-api)
 * const assetBuilder = new ApiUrlBuilder({
 *   auditLogApi: { baseUrl: 'http://localhost:3900' },
 *   deviceApi: { baseUrl: 'http://localhost:3500' },
 *   groupingApi: { baseUrl: 'http://localhost:3700' },
 *   jobsApi: { baseUrl: 'http://localhost:8080' },
 *   userApi: { baseUrl: 'http://localhost:3600' },
 * });
 * ```
 */
export class ApiUrlBuilder {
  constructor(private readonly configs: AnyApiConfigs) {}

  /**
   * Base URL to use as Cloud Run IAM ID token `audience` for a configured service
   * (scheme + host, no `/api` path — matches {@link ApiConfig.baseUrl}).
   */
  getCloudRunAudienceFor(api: string): string {
    const config = (this.configs as unknown as Record<string, ApiConfig | undefined>)[
      api
    ];
    if (!config?.baseUrl) {
      throw new Error(`API configuration not found for: ${api}`);
    }
    // Strip trailing slashes with a linear scan (no backtracking regex) so the
    // audience origin is stable. Avoids the polynomial-regex CodeQL finding.
    const trimmed = config.baseUrl.trim();
    let end = trimmed.length;
    while (end > 0 && trimmed[end - 1] === '/') {
      end--;
    }
    return trimmed.slice(0, end);
  }

  /**
   * Build a URL for any API
   *
   * @param api - The API name (must exist in configs)
   * @param controller - The controller path (e.g., 'users', 'devices')
   * @param route - The route constant (e.g., USERS_ROUTES.GET_BY_ID)
   * @param params - Object mapping param names to values (without ':' prefix)
   * @returns Fully constructed URL
   */
  build(
    api: string,
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    const config = (this.configs as unknown as Record<string, ApiConfig | undefined>)[api];
    if (!config) {
      throw new Error(`API configuration not found for: ${String(api)}`);
    }

    const prefix = config.apiPrefix || '';

    // Start with base URL and add prefix if present
    let path = `${config.baseUrl}${prefix}`;

    // Add controller if not empty
    if (controller) {
      path += `/${controller}`;
    }

    // Add route if not empty
    if (route) {
      path += `/${route}`;
    }

    // Replace route parameters with actual values
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        // Support both :key and key format
        const paramPattern = new RegExp(`:${key}(?=/|$)`, 'g');
        path = path.replace(paramPattern, String(value));
      }
    }

    // Clean up: remove double slashes (except after protocol://)
    path = path.replace(/([^:]\/)\/+/g, '$1');

    // Remove trailing slash
    path = path.replace(/\/$/, '');

    return path;
  }

  /**
   * Convenience method for Access API
   */
  accessApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('accessApi', controller, route, params);
  }

  /**
   * Convenience method for Asset Service Layer API
   */
  assetServiceLayerApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('assetServiceLayerApi', controller, route, params);
  }

  /**
   * Convenience method for Audit Log API
   */
  auditLogApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('auditLogApi', controller, route, params);
  }

  /**
   * Convenience method for Backend API
   */
  backendApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('backendApi', controller, route, params);
  }

  /**
   * Convenience method for Device API
   */
  deviceApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('deviceApi', controller, route, params);
  }

  /**
   * Convenience method for Grouping API
   */
  groupingApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('groupingApi', controller, route, params);
  }

  /**
   * Convenience method for Gopher Buddy API
   */
  gopherBuddyApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('gopherBuddyApi', controller, route, params);
  }

  /**
   * Convenience method for Jobs API
   */
  jobsApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('jobsApi', controller, route, params);
  }

  /**
   * Convenience method for User API
   */
  userApi(
    controller: string,
    route: string,
    params?: Record<string, string | number>
  ): string {
    return this.build('userApi', controller, route, params);
  }
}
